import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../../ui/carousel';
import type { RecentClimbPhoto } from '../../../types/climb';

interface ClimbRecentPhotosProps {
  photos: RecentClimbPhoto[];
  onPick: (photo: RecentClimbPhoto) => void;
}

/**
 * The walls this gym has just been photographed on, as a row to swipe through.
 *
 * A carousel rather than a grid because it is an offer, not the decision: the
 * camera and the gallery are the two ways in, and this sits under them for the
 * common case where the picture already exists. Three thumbnails at a time say
 * there are more without taking the screen from the two buttons above.
 *
 * No captions. A setter recognises the wall, not the name of whichever problem
 * happens to be credited with the shot — that name is on the button for anyone
 * reading with a screen reader, where a picture says nothing.
 */
export function ClimbRecentPhotos({ photos, onPick }: ClimbRecentPhotosProps) {
  const { t } = useTranslation();

  return (
    <RecentPictureRow
      title={t('climbEditor.photo.recent')}
      testId="climb-recent-photos"
      pictures={photos.map((photo) => ({
        key: photo.climb_id,
        label: photo.climb_name,
        thumbnailUrl: photo.thumbnail_url || photo.url,
        onPick: () => onPick(photo),
      }))}
    />
  );
}

/** One thumbnail of a row: a wall the place already has a picture of. */
export interface RecentPicture {
  key: string;
  /** What the button is called — the picture says nothing to a screen reader. */
  label: string;
  thumbnailUrl: string | null;
  /** Laid over the corner of the thumbnail: what else picking it brings. */
  badge?: ReactNode;
  onPick: () => void;
}

/**
 * A titled row of wall thumbnails under the camera and the gallery — the last
 * climbs' photos, and the scanned walls. Nothing at all when it has none.
 */
export function RecentPictureRow({
  title,
  testId,
  pictures,
}: {
  title: string;
  testId: string;
  pictures: RecentPicture[];
}) {
  if (pictures.length === 0) return null;

  return (
    <div className="space-y-2 text-left" data-testid={testId}>
      <p className="px-1 text-xs font-medium text-muted-foreground">{title}</p>
      <Carousel opts={{ align: 'start', loop: false, dragFree: true }} className="w-full">
        <CarouselContent className="-ml-2">
          {pictures.map((picture) => (
            <CarouselItem key={picture.key} className="basis-1/3 pl-2">
              <button
                type="button"
                onClick={picture.onPick}
                aria-label={picture.label}
                className="relative w-full overflow-hidden rounded-md border transition-colors hover:border-primary"
              >
                <img
                  src={picture.thumbnailUrl || ''}
                  alt=""
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover"
                />
                {picture.badge}
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
